<template>
  <div class="log-session stack tight">
    <UiPanel>
      <div class="header">
        <div style="min-width: 0;">
          <h3 class="title">{{ ICONS.sessions.log }} Activity Log</h3>
          <div class="subtitle text-muted">A future persistent log surface. For now: recent notifications.</div>
        </div>
        <div class="header-actions">
          <UiChip variant="muted" class="stat-mono num">{{ formatNumber(filtered.length) }}</UiChip>
          <UiButton size="sm" variant="secondary" :disabled="entries.length === 0" @click="clear">Clear</UiButton>
        </div>
      </div>

      <div class="controls">
        <label class="search">
          <span class="sr-only">Search activity log</span>
          <input v-model="query" class="ui-input" placeholder="Search…" />
        </label>
      </div>
    </UiPanel>

    <UiCallout v-if="entries.length === 0" variant="info">No recent activity yet.</UiCallout>

    <UiPanel v-else>
      <UiSectionHeader>
        <template #title>Recent</template>
      </UiSectionHeader>

      <div class="list">
        <div v-for="entry in filtered" :key="entry.id" class="row">
          <div class="row-icon" aria-hidden="true">{{ iconFor(entry.kind) }}</div>
          <div class="row-main">
            <div class="row-message">{{ entry.message }}</div>
            <div class="row-meta text-muted stat-mono num">{{ formatRelative(entry.at) }}</div>
          </div>
        </div>
      </div>
    </UiPanel>
  </div>
</template>

<script setup lang="ts">
import { computed, ref } from "vue";
import { ICONS } from "../../../lib/icons";
import { formatNumber } from "../../../lib/format";
import { useActivityLog, type ActivityKind } from "../../../composables/useActivityLog";
import UiButton from "../../../components/ui/UiButton.vue";
import UiCallout from "../../../components/ui/UiCallout.vue";
import UiChip from "../../../components/ui/UiChip.vue";
import UiPanel from "../../../components/ui/UiPanel.vue";
import UiSectionHeader from "../../../components/ui/UiSectionHeader.vue";

const { entries, clear } = useActivityLog();
const query = ref("");

const filtered = computed(() => {
  const q = query.value.trim().toLowerCase();
  if (!q) return entries.value;
  return entries.value.filter((e) => e.message.toLowerCase().includes(q) || e.kind.toLowerCase().includes(q));
});

function iconFor(kind: ActivityKind): string {
  if (kind === "item") return ICONS.toasts.item;
  if (kind === "xp") return ICONS.toasts.xp;
  if (kind === "success" || kind === "combat-good") return ICONS.toasts.success;
  if (kind === "warning") return ICONS.toasts.warning;
  if (kind === "error" || kind === "combat-bad") return ICONS.toasts.error;
  return ICONS.toasts.info;
}

function formatRelative(ts: number): string {
  const delta = Date.now() - ts;
  if (delta < 8_000) return "just now";
  if (delta < 60_000) return `${Math.round(delta / 1000)}s ago`;
  if (delta < 3_600_000) return `${Math.round(delta / 60_000)}m ago`;
  return `${Math.round(delta / 3_600_000)}h ago`;
}
</script>

<style scoped>
.log-session {
  min-width: 0;
}

.header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: var(--space-md);
}

.title {
  margin: 0;
  font-family: var(--font-display);
  letter-spacing: 0.02em;
}

.subtitle {
  font-size: 0.9rem;
  margin-top: 0.15rem;
}

.header-actions {
  display: flex;
  align-items: center;
  gap: var(--space-sm);
}

.controls {
  margin-top: var(--space-md);
}

.list {
  display: grid;
  gap: 0.55rem;
  margin-top: var(--space-sm);
}

.row {
  display: grid;
  grid-template-columns: 36px 1fr;
  gap: 0.75rem;
  align-items: center;
  padding: 0.65rem 0.75rem;
  border-radius: var(--radius-1);
  border: 1px solid var(--border-0);
  background: color-mix(in srgb, var(--bg-2) 78%, transparent);
}

.row-icon {
  width: 36px;
  height: 36px;
  border-radius: var(--radius-1);
  display: grid;
  place-items: center;
  background: color-mix(in srgb, var(--bg-1) 75%, transparent);
  border: 1px solid var(--border-0);
  font-size: 1.05rem;
}

.row-main {
  min-width: 0;
  display: grid;
  gap: 0.12rem;
}

.row-message {
  font-weight: 650;
  white-space: normal;
  word-break: break-word;
}

.row-meta {
  font-size: 0.8rem;
}
</style>
